import { assertNotNullObject, assertString } from 'helpers/utils/checkType';

import {
  CreditsFunnels,
  Funnels,
  FunnelsParams,
  funnelsParams,
} from './funnels';

export function eqFunnel<T extends Funnels | CreditsFunnels>(constFunnel: T) {
  return function compareInner(funnel: Funnels | CreditsFunnels): funnel is T {
    return constFunnel === funnel;
  };
}

export const assertFunnel = (funnel: unknown): funnel is Funnels => {
  if (!assertString(funnel)) {
    return false;
  }

  return Object.values({ ...Funnels, ...CreditsFunnels }).includes(
    funnel as Funnels
  );
};

export const assertFunnelParams = (obj: unknown): obj is FunnelsParams => {
  if (!assertNotNullObject(obj)) {
    return false;
  }

  return Object.keys(obj).some((key) =>
    (funnelsParams as string[]).includes(key)
  );
};

type MessageFunnels =
  | Funnels.Message
  | Funnels.BlurMessage
  | Funnels.BannerMessage
  | Funnels.PhotoMessage
  | Funnels.StickerMessage
  | Funnels.StickerMessageV2;

type ActivityFunnels =
  | Funnels.ActivityLike
  | Funnels.ActivityVisit
  | Funnels.Matches;

type VideoFunnels =
  | CreditsFunnels.ProfileVideo
  | CreditsFunnels.FinderPreview
  | CreditsFunnels.VideoCast;

export const assertActivityLikeFunnel = eqFunnel(Funnels.ActivityLike);
export const assertPrivateAlbumFunnel = eqFunnel(Funnels.PrivateAlbum);
export const assertSendMessageFunnel = eqFunnel(Funnels.Message);
export const assertBlurMessageFunnel = eqFunnel(Funnels.BlurMessage);
export const assertBannerMessageFunnel = eqFunnel(Funnels.BannerMessage);
export const assertPhotoMessageFunnel = eqFunnel(Funnels.PhotoMessage);
export const assertStickerMessageFunnel = eqFunnel(Funnels.StickerMessage);
export const assertStickerMessageV2Funnel = eqFunnel(Funnels.StickerMessageV2);
export const assertMessageFailFunnel = eqFunnel(Funnels.MessageFailed);
export const assertActivityVisitFunnel = eqFunnel(Funnels.ActivityVisit);
export const assertIntroSkipFunnel = eqFunnel(Funnels.IntroSkip);
export const assertXSkipFunnel = eqFunnel(Funnels.XSkip);
export const assertNonSkipFunnel = eqFunnel(Funnels.NonSkip);
export const assertNonSkip5MinFunnel = eqFunnel(Funnels.NonSkip5Min);
export const assertGeneralButtonFunnel = eqFunnel(Funnels.GeneralButton);
export const assertHardPaymentFunnel = eqFunnel(Funnels.HardPayment);
export const assertWelcomeMessageFunnel = eqFunnel(Funnels.WelcomeMessage);
export const assertLimitOfferFunnel = eqFunnel(Funnels.LimitOffer);
export const assertActivityMatchesFunnel = eqFunnel(Funnels.ActivityMatches);
export const assertMatchFullScreenFunnel = eqFunnel(Funnels.MatchFullScreen);
export const assertStopExitFunnel = eqFunnel(Funnels.StopExit);
export const assertStickyGiftFunnel = eqFunnel(Funnels.StickyGift);
export const assertUnlockProfileFunnel = eqFunnel(Funnels.ProfileUnlock);
export const assertPayMessageFunnel = eqFunnel(CreditsFunnels.PayMessages);
export const assertUnlockProfileVideoFunnel = eqFunnel(
  CreditsFunnels.ProfileVideo
);
export const assertUnlockFinderPreviewVideoFunnel = eqFunnel(
  CreditsFunnels.FinderPreview
);
export const assertUnlockVideoCastVideoFunnel = eqFunnel(
  CreditsFunnels.VideoCast
);
export const assertFinderLimitFunnel = eqFunnel(Funnels.FinderLimit);
export const assertLikesButton = eqFunnel(Funnels.LikesButton);
export const assertFinderButton = eqFunnel(Funnels.FinderButton);
export const assertThreadsButton = eqFunnel(Funnels.ThreadsButton);
export const assertMyProfileGetButton = eqFunnel(Funnels.MyProfileGetButton);
export const assertMyProfilePremiumButton = eqFunnel(
  Funnels.MyProfilePremiumButton
);
export const assertMyProfileCarousel = eqFunnel(Funnels.MyProfileCarousel);
export const assertRevealLikes = eqFunnel(Funnels.RevealLikes);
export const assertRevealVisits = eqFunnel(Funnels.RevealVisits);
export const assertMyProfileBanner = eqFunnel(Funnels.ProfileBanner);
export const assertMyProfileButton = eqFunnel(Funnels.MyProfileButton);
export const assertMyNotification = eqFunnel(Funnels.Notifications);
export const assertEditProfile = eqFunnel(Funnels.EditProfile);
export const assertNotificationsLikes = eqFunnel(Funnels.NotificationsLikes);

export const assertMessageFunnel = (
  funnelName: Funnels
): funnelName is MessageFunnels => {
  return (
    assertSendMessageFunnel(funnelName) ||
    assertBlurMessageFunnel(funnelName) ||
    assertBannerMessageFunnel(funnelName) ||
    assertPhotoMessageFunnel(funnelName) ||
    assertMessageFailFunnel(funnelName)
  );
};

export const assertActivityFunnel = (
  funnelName: Funnels
): funnelName is ActivityFunnels => {
  return (
    assertActivityLikeFunnel(funnelName) ||
    assertActivityVisitFunnel(funnelName) ||
    assertActivityMatchesFunnel(funnelName)
  );
};

export const assertVideoFunnel = (
  funnelName: Funnels | CreditsFunnels
): funnelName is VideoFunnels => {
  return (
    assertUnlockProfileVideoFunnel(funnelName) ||
    assertUnlockFinderPreviewVideoFunnel(funnelName) ||
    assertUnlockVideoCastVideoFunnel(funnelName)
  );
};
